import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Check, Link2, Ban, Loader2 } from "lucide-react";

export default function ShareDialog({ workspaceId, workspaceName, open, onClose }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !workspaceId) return;
    setLoading(true);
    api.post(`/workspaces/${workspaceId}/share`)
      .then(({ data }) => setLink(data))
      .catch(() => toast.error("Couldn't create share link"))
      .finally(() => setLoading(false));
  }, [open, workspaceId]);

  const url = link ? `${window.location.origin}/share/${link.token}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(false), 1600);
  };

  const revoke = async () => {
    await api.delete(`/workspaces/${workspaceId}/share`);
    setLink(null);
    toast.success("Share link revoked");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Link2 className="h-4 w-4" /> Share “{workspaceName}”
          </DialogTitle>
          <DialogDescription className="text-xs">
            Anyone with this link gets a live, read-only view of this workspace. Perfect for investors and clients. Nothing can be edited.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : link ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input readOnly value={url} className="h-9 font-mono text-xs" data-testid="share-link-input" />
              <Button size="icon" className="h-9 w-9 shrink-0" onClick={copy} data-testid="share-copy-button">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <Button variant="ghost" size="sm" className="h-8 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={revoke} data-testid="share-revoke-button">
              <Ban className="mr-1.5 h-3.5 w-3.5" /> Revoke link
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
